import React from 'react'
import { Admin, Resource, Authenticated } from 'react-admin'
import { Route } from 'react-router-dom'
import languageSpanish from '../../utils/languageSpanish'

import authProvider from '../../utils/authProvider'
import dataProvider from '../../utils/dataProvider'
import rootSaga from '../../sagas'
import errorSagas from '../../sagas/errorHandler'

import LoginPage from '../../components/Login'
import ChangePasswordForm from '../../components/Login/ChangePasswordForm'
import ForgetPasswordForm from '../../components/Login/ForgetPasswordForm'
import CustomLayout from '../../components/Layout'

import { Dashboard } from '../../components/Dashboard'

import { InstalacionesList, EstructurasList, EDAsList } from '../../components/Activos'
import { MantenedorList } from '../../components/Mantenedor'
import { Certification } from '../../components/Certification'
import { Reportes } from '../../components/Reportes'
import { ReportesProyectos } from '../../components/ReportesProyectos'
import { DBBackup } from '../../components/DBBackup'
import { ValidarInstalaciones } from '../../components/ValidarInstalaciones'

import { UsuarioList, UsuarioEdit, UsuarioCreate, UsuarioShow } from '../../components/Usuario'
import { UsuarioCoordinadoList, UsuarioCoordinadoEdit, UsuarioCoordinadoCreate, UsuarioCoordinadoShow } from '../../components/UsuarioCoordinado'
import { PermisoList, PermisoEdit, PermisoCreate, PermisoShow } from '../../components/Permiso'
import { 
  ComunaList, ComunaEdit, ComunaShow, ComunaCreate, 
  ProvinciaList, ProvinciaEdit, ProvinciaShow, ProvinciaCreate, 
  RegionList, RegionEdit, RegionShow, RegionCreate, 
} from '../../components/Geografia'

import { EsquemaList, EsquemaEdit, EsquemaCreate } from '../../components/Esquema'
import { FichaTecnicaList, FichaTecnicaEdit } from '../../components/FichaTecnica'
import { DatoTecnicoList, DatoTecnicoEdit, DatoTecnicoCreate, DatoTecnicoShow } from '../../components/DatoTecnico'
import { DecretoList, DecretoEdit, DecretoCreate, DecretoShow } from '../../components/Decreto'
import { CategoriaVUList, CategoriaVUEdit, CategoriaVUCreate, CategoriaVUShow } from '../../components/CategoriaVidaUtil'
import { SistemaInterconectadoList, SistemaInterconectadoEdit, SistemaInterconectadoCreate, SistemaInterconectadoShow } from '../../components/SistemaInterconectado'
import { SistemaElectricoList, SistemaElectricoEdit, SistemaElectricoCreate, SistemaElectricoShow } from '../../components/SistemaElectrico'

import { NotificationList, NotificationCreate, NotificationEdit } from '../../components/Notifications'

import { GenericResource } from '../../components/Generic'

const routes = [
  <Route exact path="/mantenedor" component={MantenedorList} />,
  <Route exact path="/certificacion" component={Certification} />,
  <Route exact path="/reportes" component={Reportes} />,
  <Route exact path="/reportes-proyectos" component={ReportesProyectos} />,
  <Route exact path="/validar-instalaciones" component={ValidarInstalaciones} />,
  <Route exact path="/descarga-base-datos" component={DBBackup} />,
  <Route exact path="/change_password" render={() => <Authenticated authParams={{ required: true}}><ChangePasswordForm/></Authenticated>} noLayout/>,
  <Route exact path="/forget_password" component={ForgetPasswordForm} noLayout/>
  // <Route exact path="/resetPassword" component={ResetPassword}/>
  // <Route exact path="/foo" component={Foo} />,             -> default layout with menu & navbar
  // <Route exact path="/bar" component={Bar} noLayout />,    -> no layout
]

const messages = {
  'es': languageSpanish,
};

const i18nProvider = locale => messages[locale]

const App = () => (
  <Admin
    customSagas={[ rootSaga, errorSagas ]}
    customRoutes={routes}
    authProvider={authProvider}
    dataProvider={dataProvider}
    loginPage={LoginPage}
    appLayout={CustomLayout}
    dashboard={Dashboard}
    locale="es" 
    i18nProvider={i18nProvider}
  >
    {permissions => [
      permissions.isUserAdmin
        ? (
          <Resource
            name="usuarios-coordinador"
            list={UsuarioList}
            edit={UsuarioEdit}
            show={UsuarioShow}
            create={UsuarioCreate}
          />
        )
        : null,
      permissions.isUserAdmin
        ? (
          <Resource
            name="usuarios-coordinados"
            list={UsuarioCoordinadoList}
            edit={UsuarioCoordinadoEdit}
            show={UsuarioCoordinadoShow}
            create={UsuarioCoordinadoCreate}
          />
        )
        : null,
      permissions.isUserAdmin
        ? (
          <Resource
            name="permisos"
            list={PermisoList}
            edit={PermisoEdit}
            show={PermisoShow}
            create={PermisoCreate}
          />
        )
        : null,
      ["admin", "coordinador"].includes(permissions.role)
        ? (
          <Resource
            name="esquemas-datos-tecnicos"
            list={EsquemaList}
            create={["admin", "coordinador"].includes(permissions.role) ? EsquemaCreate : null}
            edit={["admin", "coordinador"].includes(permissions.role) ? EsquemaEdit : null}
          />
        )
        : null,
      ["admin", "coordinador"].includes(permissions.role)
        ? (
          <Resource
            name="esquemas-fichas-tecnicas"
            list={["admin", "coordinador"].includes(permissions.role) ? FichaTecnicaList : null}
            edit={["admin", "coordinador"].includes(permissions.role) ? FichaTecnicaEdit : null}
          />
        )
        : null,
      <Resource
        name="datos-tecnicos"
        list={DatoTecnicoList}
        create={["admin", "coordinador"].includes(permissions.role) ? DatoTecnicoCreate : null}
        edit={["admin", "coordinador"].includes(permissions.role) ? DatoTecnicoEdit : null}
        show={DatoTecnicoShow}
      />,
      <Resource
        name="decretos"
        list={DecretoList}
        create={["admin", "coordinador"].includes(permissions.role) ? DecretoCreate : null}
        edit={["admin", "coordinador"].includes(permissions.role) ? DecretoEdit : null}
        show={DecretoShow}
      />,
      <Resource
        name="sistemas-interconectados"
        list={SistemaInterconectadoList}
        create={["admin", "coordinador"].includes(permissions.role) ? SistemaInterconectadoCreate : null}
        edit={["admin", "coordinador"].includes(permissions.role) ? SistemaInterconectadoEdit : null}
        show={SistemaInterconectadoShow}
      />,
      <Resource
        name="sistemas-electricos"
        list={SistemaElectricoList}
        create={["admin", "coordinador"].includes(permissions.role) ? SistemaElectricoCreate : null}
        edit={["admin", "coordinador"].includes(permissions.role) ? SistemaElectricoEdit : null}
        show={SistemaElectricoShow}
      />,
      <Resource
        name="categorias-vida-util"
        list={CategoriaVUList}
        create={["admin", "coordinador"].includes(permissions.role) ? CategoriaVUCreate : null}
        edit={["admin", "coordinador"].includes(permissions.role) ? CategoriaVUEdit : null}
        show={CategoriaVUShow}
        />,
      <Resource
        name="notificaciones"
        list={["admin"].includes(permissions.role) ? NotificationList : null}
        create={["admin"].includes(permissions.role) ? NotificationCreate : null}
        edit={["admin"].includes(permissions.role) ? NotificationEdit : null}
        // show={CategoriaVUShow}
      />,

      <Resource name="documentos" />,
      <Resource name="instalaciones" list={InstalacionesList} />,
      <Resource name="eias" list={EDAsList} />,
      
      <GenericResource name="grupos" permissions={permissions}/>,
      <GenericResource name="empresas" permissions={permissions}/>,
      // <GenericResource name="Decretos" permissions={permissions}/>,
      <Resource
          name="comunas"
          list={ComunaList}
          show={ComunaShow}
          create={["admin", "coordinador"].includes(permissions.role) ? ComunaCreate : null}
          edit={["admin", "coordinador"].includes(permissions.role) ? ComunaEdit : null}
        />,
      <Resource
          name="provincias"
          list={ProvinciaList}
          show={ProvinciaShow}
          create={["admin", "coordinador"].includes(permissions.role) ? ProvinciaCreate : null}
          edit={["admin", "coordinador"].includes(permissions.role) ? ProvinciaEdit : null}
        />,
      <Resource
          name="regiones"
          list={RegionList}
          show={RegionShow}
          create={["admin", "coordinador"].includes(permissions.role) ? RegionCreate : null}
          edit={["admin", "coordinador"].includes(permissions.role) ? RegionEdit : null}
        />,
        
      // Activos resources
      <Resource name="estructuas-y-oocc" list={EstructurasList} />,
      <GenericResource name="bancos-condensadores" permissions={permissions}/>,
      <GenericResource name="compensadores-estaticos-reactivos" permissions={permissions}/>,
      <GenericResource name="condensadores-series" permissions={permissions}/>,
      <GenericResource name="condensadores-sincronos" permissions={permissions}/>,
      <GenericResource name="reactores" permissions={permissions}/>,
      <GenericResource name="compensadores-activos" permissions={permissions}/>,
      <GenericResource name="sistemas-protecciones" permissions={permissions}/>,
      // <GenericResource name="retiro-instalaciones" permissions={permissions}/>,
      <GenericResource name="pararrayos" permissions={permissions}/>,
      <GenericResource name="interruptores" permissions={permissions}/>,
      <GenericResource name="dispositivos-reconexiones" permissions={permissions}/>,
      <GenericResource name="trampas-ondas" permissions={permissions}/>,
      <GenericResource name="desconectadores" permissions={permissions}/>,
      <GenericResource name="medidores" permissions={permissions}/>,
      // <GenericResource name="sincronizadores" permissions={permissions}/>,
      <GenericResource name="equipos-principales-comunicacion" permissions={permissions}/>,
      // <GenericResource name="conexiones-tierra" permissions={permissions}/>,
      <GenericResource name="torres" permissions={permissions}/>,
      <GenericResource name="estructuras-lineas" permissions={permissions}/>,
      <GenericResource name="estructuras-subestaciones" permissions={permissions}/>,
      <GenericResource name="accesorios-estructuras-lineas-aereas" permissions={permissions}/>,
      <GenericResource name="accesorios-estructuras-lineas-subterraneas" permissions={permissions}/>,
      <GenericResource name="accesorios-estructuras-subestaciones" permissions={permissions}/>,
      <GenericResource name="vanos" permissions={permissions}/>,
      <GenericResource name="tuneles" permissions={permissions}/>,
      <GenericResource name="camaras" permissions={permissions}/>,
      <GenericResource name="marcos-lineas" permissions={permissions}/>,
      <GenericResource name="patios-mufas" permissions={permissions}/>,
      <GenericResource name="mufas" permissions={permissions}/>,
      <GenericResource name="pararrayos-mufas" permissions={permissions}/>,
      // <GenericResource name="amortiguadores" permissions={permissions}/>,
      // <GenericResource name="espaciadores" permissions={permissions}/>,
      <GenericResource name="accesorios-vanos" permissions={permissions}/>,
      // <GenericResource name="Conductores" permissions={permissions}/>,
      // <GenericResource name="monitores-lineas" permissions={permissions}/>,
      // <GenericResource name="boyas-senalizacion" permissions={permissions}/>,
      // <GenericResource name="cables-guardia" permissions={permissions}/>,
      <GenericResource name="aisladores" permissions={permissions}/>,
      <GenericResource name="paneles-antirruido" permissions={permissions}/>,
      <GenericResource name="sistemas-control-pano" permissions={permissions}/>,
      <GenericResource name="elementos-comunes-panos" permissions={permissions}/>,
      <GenericResource name="bancos-baterias" permissions={permissions}/>,
      <GenericResource name="generadores-energia" permissions={permissions}/>,
      <GenericResource name="transformadores-ssaa" permissions={permissions}/>,
      // <GenericResource name="semiconductores" permissions={permissions}/>,
      <GenericResource name="salas-control-y-protecciones" permissions={permissions}/>,
      <GenericResource name="elementos-comunes-ssee" permissions={permissions}/>,
      <GenericResource name="elementos-comunes-patios" permissions={permissions}/>,
      // <GenericResource name="bienes-equipos-vehiculos" permissions={permissions}/>,
      <GenericResource name="estudios-impacto-ambiental" permissions={permissions}/>,
      <GenericResource name="mitigaciones-ambientales" permissions={permissions}/>,
      <GenericResource name="estaciones-repetidoras" permissions={permissions}/>,
      <GenericResource name="oocc" permissions={permissions}/>,
      <GenericResource name="tipos-oocc" permissions={permissions}/>,
      <GenericResource name="servidumbres" permissions={permissions}/>,
      <GenericResource name="subestaciones" permissions={permissions}/>,
      <GenericResource name="circuitos" permissions={permissions}/>,
      // <GenericResource name="taps" permissions={permissions}/>,
      <GenericResource name="lineas" permissions={permissions}/>,
      <GenericResource name="tramos" permissions={permissions}/>,
      <GenericResource name="patios-subestaciones" permissions={permissions}/>,
      <GenericResource name="barras" permissions={permissions}/>,
      <GenericResource name="secciones-barras" permissions={permissions}/>,
      <GenericResource name="panos" permissions={permissions}/>,
      <GenericResource name="transformadores-2d" permissions={permissions}/>,
      <GenericResource name="transformadores-3d" permissions={permissions}/>,
      <GenericResource name="transformadores-corriente" permissions={permissions}/>,
      <GenericResource name="transformadores-potencial" permissions={permissions}/>,
      <GenericResource name="casas-servicios-generales-y-casetas" permissions={permissions}/>,
      <GenericResource name="tableros" permissions={permissions}/>,
      <GenericResource name="elementos-comunes-ssgg-casetas" permissions={permissions}/>,
      <GenericResource name="armarios" permissions={permissions}/>,
      <GenericResource name="multiplexadores" permissions={permissions}/>,
      // <GenericResource name="equipos-mmoo" permissions={permissions}/>,
      // <GenericResource name="equipos-oplat" permissions={permissions}/>,
      <GenericResource name="otros-elementos-telecomunicaciones" permissions={permissions}/>,
      <GenericResource name="reles-proteccion" permissions={permissions}/>,
      <GenericResource name="otros-elementos-sistemas-proteccion" permissions={permissions}/>,
      <GenericResource name="controladores-bahia" permissions={permissions}/>,
      <GenericResource name="medidores" permissions={permissions}/>,
      <GenericResource name="otros-elementos-sistemas-control" permissions={permissions}/>,
      // <GenericResource name="servicios-auxiliares-ca-y-cc" permissions={permissions}/>,
      <GenericResource name="elementos-comunes-armarios" permissions={permissions}/>,
      <GenericResource name="sistemas-respaldo-energia" permissions={permissions}/>,
      <GenericResource name="condensadores-acoplamiento" permissions={permissions}/>,
      <GenericResource name="celdas" permissions={permissions}/>,
      <GenericResource name="transformadores-zigzag" permissions={permissions}/>,
    ]}
  </Admin>
)

export default App;
